import _ from "lodash";
import { Observable, of, forkJoin, zip } from "rxjs";
import { catchError, switchMap, map, tap } from 'rxjs/operators'

import { AssociatedMediasResource, MediaMetaDataResource, RecordMaskResource, MetaRecordMaskResource, MediaUploadResult2 } from "@skinsoft/types";
import { EditScreenVCSaveResult, EditScreenViewControllerInterface } from "@skinsoft/classes-interfaces-definition";
import { DataStore } from "@skinsoft/data-store";
import { MaskFieldUpdateEvent, MaskFieldUpdateEventType } from "@skinsoft/record-mask/src/components/mask-field";
import { ImageTransferHelper, MediaTransferHelper } from "@skinsoft/media-transfer-helper";

declare const config
const FIND_MEDIAS_MAX_TRY = config.editScreen.mediaUpload.maxAttempts
const FIND_MEDIAS_REPEAT_DELAY = config.editScreen.mediaUpload.retryDelay

export class EntityEditScreenController<T extends { id: string, associatedMedias: AssociatedMediasResource }> implements EditScreenViewControllerInterface<T> {
    
    private dataStore: DataStore<T>
    private imageTransferHelper: ImageTransferHelper
    private mediaTransferHelper: MediaTransferHelper

    constructor(dataStore: DataStore<T>) {
        this.dataStore = dataStore
        this.mediaTransferHelper = MediaTransferHelper.getInstance()
        this.imageTransferHelper = ImageTransferHelper.getInstance()
    }
    
    getEntity(id: string): Observable<T> {
        return this.dataStore.fetchOne(id)
    }

    getCachedMediasKeys(entity: T): string[] {
        const res: string[] = []

        if(entity.associatedMedias) {
            if(entity.associatedMedias.first && MediaTransferHelper.isLocalMedia(entity.associatedMedias.first)) {
                res.push(entity.associatedMedias.first)
            }

            if(entity.associatedMedias.second && MediaTransferHelper.isLocalMedia(entity.associatedMedias.second)) {
                res.push(entity.associatedMedias.second)
            }

            if(entity.associatedMedias.third && MediaTransferHelper.isLocalMedia(entity.associatedMedias.third)) {
                res.push(entity.associatedMedias.third)
            }

            if(entity.associatedMedias.medias?.length) {
                for(const mediaId of entity.associatedMedias.medias) {
                    if(MediaTransferHelper.isLocalMedia(mediaId)) {
                        res.push(mediaId)
                    }
                }
            }
        }

        return _.uniq(res)
    }

    uploadImage(entity: T, mediaCacheKey: string): Observable<MediaUploadResult2> {
        const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(mediaCacheKey)

        if(mediaId) {
            this.updateMediaId(entity, mediaCacheKey, mediaId)
            return of({
                cacheKey: mediaCacheKey,
                mediaId: mediaId,
            } as MediaUploadResult2)
        } else {
            return this.imageTransferHelper.uploadLocalImageToApi(mediaCacheKey).pipe(
                catchError(err => {
                    return of({
                        cacheKey: mediaCacheKey,
                        errorOccured: true,
                        timeOut: false,
                    } as MediaUploadResult2)
                }),
                switchMap(uploadResult => {
                    if(uploadResult.fileName && !uploadResult.errorOccured) {
                        return zip(
                            this.mediaTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                                uploadResult.fileName,
                                FIND_MEDIAS_MAX_TRY,
                                FIND_MEDIAS_REPEAT_DELAY,
                            ),
                            of(uploadResult),
                        )
                    } else {
                        uploadResult.errorOccured = true

                        return zip(
                            of(null),
                            of(uploadResult)
                        )
                    }
                }),
                map(([media, uploadResult]) => {
                    if(!uploadResult.errorOccured) {
                        if(media?.id) {
                            uploadResult.mediaId = media.id
                        } else {
                            uploadResult.timeOut = true
                            uploadResult.errorOccured = true
                        }
                    }

                    return uploadResult
                }),
                tap(uploadResult => {
                    if(uploadResult.mediaId && uploadResult.cacheKey) {
                        this.mediaTransferHelper.saveMappingCacheKeyMediaId(
                            uploadResult.cacheKey, 
                            uploadResult.mediaId
                        )
                        this.updateMediaId(entity, uploadResult.cacheKey, uploadResult.mediaId)
                    }
                })
            )
        }
    }

    uploadImages(entity: T): Observable<MediaUploadResult2[]> {
        const cachedMediasKeys = this.getCachedMediasKeys(entity)

        if(cachedMediasKeys.length) {
            const mediaUploads$: Observable<MediaUploadResult2>[] = []

            for(const cacheKey of cachedMediasKeys) {
                const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(cacheKey)

                if(mediaId) {
                    this.updateMediaId(entity, cacheKey, mediaId)
                } else {
                    mediaUploads$.push(
                        this.imageTransferHelper.uploadLocalImageToApi(cacheKey).pipe(
                            catchError(err => {
                                return of({
                                    cacheKey: cacheKey,
                                    errorOccured: true,
                                    timeOut: false,
                                } as MediaUploadResult2)
                            }),
                        )
                    )
                }
            }

            return forkJoin(mediaUploads$).pipe(
                switchMap(allUploadResults => {
                    if(allUploadResults?.length === 0) {
                        return of([[], []] as [MediaMetaDataResource[], MediaUploadResult2[]])
                    }

                    const [resultsWithFileNames, resultsNoFileName] = _.partition(
                        allUploadResults,
                        (uploadResult) => {
                            return uploadResult.fileName
                        }
                    )

                    for(const uploadResult of allUploadResults) {
                        const noFileName = resultsNoFileName.find(noFileNameRes => {
                            return noFileNameRes.cacheKey === uploadResult.cacheKey
                        })

                        if(noFileName) {
                            uploadResult.errorOccured = true
                            uploadResult.timeOut = false
                        }
                    }

                    const fileNames: string[] = resultsWithFileNames.map(uploadResult => {
                        return uploadResult.fileName! // ! because we filtered with _.partition before
                    })

                    if(fileNames.length) {
                        return zip(
                            this.mediaTransferHelper.repeatFindingMediasIdsFromFileNamesWithLimit(
                                fileNames,
                                FIND_MEDIAS_MAX_TRY,
                                FIND_MEDIAS_REPEAT_DELAY,
                            ),
                            of(allUploadResults)
                        )
                    }

                    return zip(
                        of([] as MediaMetaDataResource[]),
                        of(allUploadResults),
                    )
                }),
                switchMap(([mediasMetaData, mediaUploadResults]) => {
                    for(const uploadResult of mediaUploadResults) {
                        const metaData = mediasMetaData.find(metaData => {
                            return metaData.metaData?.fileRecordCS?.fileName === uploadResult.fileName
                        })
                        
                        if(metaData) {
                            uploadResult.mediaId = metaData.id
                            if(uploadResult.cacheKey) {
                                this.mediaTransferHelper.saveMappingCacheKeyMediaId(
                                    uploadResult.cacheKey,
                                    metaData.id,
                                )
                            }
                        } else if(!uploadResult.errorOccured) {
                            uploadResult.errorOccured = true
                            uploadResult.timeOut = true
                        }
                    }
                    return of(mediaUploadResults)
                })
            )
        }

        return of([])
    }

    private updateMediaId(entity: T, oldId: string, newId: string) {
        if(entity.associatedMedias) {
            if(entity.associatedMedias.first && entity.associatedMedias.first === oldId) {
                entity.associatedMedias.first = newId
            }

            if(entity.associatedMedias.second && entity.associatedMedias.second === oldId) {
                entity.associatedMedias.second = newId
            }

            if(entity.associatedMedias.third && entity.associatedMedias.third === oldId) {
                entity.associatedMedias.third = newId
            }

            if(entity.associatedMedias.medias?.length) {
                entity.associatedMedias.medias = entity.associatedMedias.medias.map(mediaId => {
                    if(mediaId === oldId) {
                        return newId
                    }

                    return mediaId
                })
            }
        }
    }

    adaptValuesBeforeSave(entity: T, recordMasks: (RecordMaskResource | MetaRecordMaskResource)[]) {

    }

    saveEntity(entity: T, recordMasks: (RecordMaskResource | MetaRecordMaskResource)[]): Observable<EditScreenVCSaveResult> {
        this.adaptValuesBeforeSave(entity, recordMasks)

        let uploadImage$: Observable<MediaUploadResult2[]>

        if(window.navigator.onLine) {
            uploadImage$ = this.uploadImages(entity)
        } else {
            uploadImage$ = of([] as MediaUploadResult2[])
        }
        return uploadImage$.pipe(
            map(mediaUploadResults => {
                const saveResult: EditScreenVCSaveResult = {
                    success: true,
                    mediaUploadResult: mediaUploadResults,
                }

                for(const uploadResult of mediaUploadResults) {
                    if(uploadResult.cacheKey && uploadResult.mediaId) {
                        this.updateMediaId(entity, uploadResult.cacheKey, uploadResult.mediaId)
                    } else {
                        saveResult.success = false
                    }
                }

                return saveResult
            }),
            switchMap(saveResult => {
                if(saveResult.success) {
                    return zip(
                        of(saveResult),
                        this.dataStore.update(entity.id, entity),
                    )
                } else {
                    return zip(
                        of(saveResult),
                        of(null)
                    )
                }
            }),
            map(([saveResult, updatedEntity]) => {
                if(!updatedEntity?.id) {
                    saveResult.success = false
                }

                return saveResult 
            })
        )
    }

    updateEntity(event: MaskFieldUpdateEvent, entity: Partial<T>) {
        if(event.type === MaskFieldUpdateEventType.DELETE) {
            // In case of a deletion, we need to check if the deleted value
            // is inside an array. Indeed, we have to use 2 differents approaches
            // depending if we delete a field or a value inside an array.

            const lastPropInPathRegex = /(\[\d\]|\.[a-zA-Z\d]*)$/

            const path = event.fieldPath
            const pathToLastParent = path.replace(lastPropInPathRegex, '')
            const lastParent = _.get(entity, pathToLastParent)

            if(_.isArray(lastParent)) {
                // if the last parent is an array, we extract the index of the value
                // to delete from the path (it is the last "prop" in the path)

                const indexMatches = path.match(lastPropInPathRegex)
                if(indexMatches?.length) {
                    // @ts-ignore need to upgrade ts (replaceAll)
                    const indexString = indexMatches[0].replaceAll(/\[|\]|\./g, '')
                    const index = parseInt(indexString)
                    if(!isNaN(index)) {
                        lastParent.splice(index, 1)
                    } else {
                        // in case of error the user will see no action from his deletion, so no need
                        // to show an error message
                    }

                } else {
                    // in case of error the user will see no action from his deletion, so no need
                    // to show an error message
                }

            } else {
                _.unset(entity, path)
            }
        } else {
            if(entity === null) {
                entity = {}
            }
            _.set(entity, event.fieldPath, event.value)
        }
    }

    addImage(mediaId: string, entity: Partial<T>) {
        const medias = entity?.associatedMedias?.medias ?? []
    
        if(!entity) {
            entity = {}
        }
        if(!entity?.associatedMedias?.medias) {
            _.set(
                entity,
                'associatedMedias.medias',
                medias
            )
        }
        
        if(medias === null) {
            entity.associatedMedias!.medias = [mediaId]
        }
        else if(!medias.includes(mediaId)) {
            if(entity.associatedMedias?.medias) {
                entity.associatedMedias.medias.push(mediaId)
            } else {
                _.set(entity, 'associatedMedias.medias', [mediaId])
            }
        }
    }

    deleteImage(mediaToDeleteId: string, entity: T) {
        if(mediaToDeleteId && entity?.associatedMedias?.medias?.length) {
            entity.associatedMedias.medias = entity.associatedMedias.medias.filter(mediaId => {
                return mediaId !== mediaToDeleteId
            })
        }
    }

    setFirstMedia(mediaId: string, entity: T) {
        if(mediaId) {
            this.addImage(mediaId, entity) // if the media is already present it will not be added twice
            _.set(entity, "associatedMedias.first", mediaId)
        }
    }
}